import { useEffect, useState } from 'react';
import { adminApi } from '../../lib/api';
import { useAdminAuth } from './AdminLayout';
import { toast } from 'sonner';
import { Plus, Trash2, X, Tag } from 'lucide-react';

const empty = { code: '', type: 'percent', value: 10, min_order: 0, max_discount: null, is_active: true, description: '' };

export default function AdminCoupons() {
  const { password } = useAdminAuth();
  const [items, setItems] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(empty);
  const client = adminApi(password);

  const load = () => client.get('/admin/coupons').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);  // eslint-disable-line

  const save = async (e) => {
    e.preventDefault();
    try {
      await client.post('/admin/coupons', { ...form, value: Number(form.value), min_order: Number(form.min_order), max_discount: form.max_discount ? Number(form.max_discount) : null });
      toast.success('Coupon created');
      setShowForm(false); setForm(empty); load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  const remove = async (id) => {
    if (!window.confirm('Delete this coupon?')) return;
    await client.delete(`/admin/coupons/${id}`); load();
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div><h1 className="font-heading font-extrabold text-3xl text-textprimary">Coupons</h1><p className="text-textsecondary font-body text-sm">{items.length} active offers</p></div>
        <button onClick={() => setShowForm(true)} data-testid="admin-add-coupon" className="bg-saffron text-white px-4 py-2.5 rounded-2xl font-heading font-semibold shadow-warm flex items-center gap-2"><Plus className="w-4 h-4" /> New Coupon</button>
      </div>
      <div className="grid gap-3">
        {items.map((c) => (
          <div key={c.id} className="bg-white rounded-2xl p-4 border border-borderbeige shadow-card flex justify-between items-center" data-testid={`admin-coupon-${c.id}`}>
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-2xl bg-gradient-offer flex items-center justify-center"><Tag className="w-5 h-5 text-maroon" /></div>
              <div>
                <div className="font-heading font-bold text-lg text-maroon">{c.code}</div>
                <div className="text-xs text-textsecondary font-body">{c.type === 'percent' ? `${c.value}% off` : `₹${c.value} off`}{c.min_order > 0 && ` • min ₹${c.min_order}`}{c.description && ` • ${c.description}`}</div>
              </div>
            </div>
            <button onClick={() => remove(c.id)} className="text-textsecondary hover:text-maroon" data-testid={`admin-delete-coupon-${c.id}`}><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
      </div>
      {showForm && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-3xl p-6 max-w-md w-full">
            <div className="flex justify-between items-center mb-4"><h2 className="font-heading font-bold text-xl">New Coupon</h2><button type="button" onClick={() => setShowForm(false)}><X className="w-5 h-5" /></button></div>
            <div className="space-y-3">
              <input required placeholder="Code (e.g. WELCOME20)" value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="coupon-form-code" />
              <select value={form.type} onChange={(e) => setForm({ ...form, type: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="coupon-form-type">
                <option value="percent">Percent off</option><option value="flat">Flat amount off</option>
              </select>
              <input required type="number" placeholder="Value (% or ₹)" value={form.value} onChange={(e) => setForm({ ...form, value: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" data-testid="coupon-form-value" />
              <input type="number" placeholder="Min order value (₹)" value={form.min_order} onChange={(e) => setForm({ ...form, min_order: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" />
              {form.type === 'percent' && <input type="number" placeholder="Max discount cap (₹)" value={form.max_discount || ''} onChange={(e) => setForm({ ...form, max_discount: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" />}
              <input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="w-full px-3 py-2 border border-borderbeige rounded-xl" />
              <button type="submit" data-testid="coupon-form-submit" className="w-full bg-saffron text-white py-2.5 rounded-2xl font-heading font-bold shadow-warm">Create</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
